import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { idSchema } from '../validators'
import { PARTICIPATION_STATES, QUESTION_STATUSES } from '../vocab'
import { staffClientActionSchema } from './client-actions'
import { staffParticipationSchema } from './participation'
import { staffQuestionSchema } from './questions'

/**
 * The staff cross-org inbox (D-18): questions, participation requests and client
 * actions across every investor org. Client actions are read-only (R-12). Every
 * read is audited on the core chain and on each org chain it touched.
 */

export const INBOX_TABS = ['questions', 'participation', 'client_actions'] as const
export type InboxTab = (typeof INBOX_TABS)[number]

export const inboxQuerySchema = pageQuerySchema.extend({
  tab: z.enum(INBOX_TABS).default('questions'),
  org_id: idSchema.optional(),
  engagement_id: idSchema.optional(),
  /** Questions: a question status. Participation: a participation state, or `open`. */
  status: z
    .union([
      z.enum(QUESTION_STATUSES.values),
      z.enum(PARTICIPATION_STATES.values),
      z.literal('open'),
    ])
    .optional(),
  /** Only items on cases assigned to the caller (as manager). */
  mine: z.enum(['true', 'false']).optional(),
})
export type InboxQuery = z.infer<typeof inboxQuerySchema>

export const inboxCountsSchema = z.object({
  questions_awaiting_reply: z.number().int(),
  participation_open: z.number().int(),
  client_actions: z.number().int(),
})
export type InboxCounts = z.infer<typeof inboxCountsSchema>

const inboxCommon = {
  next_cursor: z.string().nullable(),
  counts: inboxCountsSchema,
  orgs: z.array(z.object({ id: z.string(), name: z.string() })),
  /** Active Fruuit managers (reassignment targets). */
  managers: z.array(z.object({ id: z.string(), name: z.string() })),
}

export const inboxPageSchema = z.discriminatedUnion('tab', [
  z.object({
    tab: z.literal('questions'),
    items: z.array(staffQuestionSchema),
    ...inboxCommon,
  }),
  z.object({
    tab: z.literal('participation'),
    items: z.array(staffParticipationSchema),
    ...inboxCommon,
  }),
  z.object({
    tab: z.literal('client_actions'),
    items: z.array(staffClientActionSchema),
    ...inboxCommon,
  }),
])
export type InboxPage = z.infer<typeof inboxPageSchema>
